import * as React from 'react'
import styled, { css, createGlobalStyle } from 'styled-components'

import * as hooks from '@owl-nest/hooks'
import * as plume from '@ulule/owl-kit-components/next'
import { t } from '@owl-nest/localize'
import { scroll } from '@owl-nest/utils'

import { PM_MENU_WIDTH } from './constants'

type ResponsiveMenuProps = {
  className?: string
  labelMargin: number
  closedMenuHeight: number
  children: React.ComponentType<{
    setActiveTopPosition: (top: number) => void
    isMobileNavOpen: boolean
    containerRef: React.RefObject<HTMLElement>
  }>
  mobileBreakpoint?: string
}

//TODO: This component was extracted from
// `frontend/owl-nest/common/project-manager-nav/src/NavMenu.tsx` The
// `ResponsiveMenu` component handles the "transformation" of the menu in an
// animated dropdown in mobile. Since the `backer-backoffice` feature needed the
// same behavior, the component was extracted. But to limit the impact on the
// existing `NavMenu` its code was not changed to use `ResponsiveMenu`. If you
// change anything in `ResponsiveMenu`, remember to port the modification back
// in `NavMenu`
export function ResponsiveMenu({
  className,
  labelMargin,
  closedMenuHeight,
  children: Content,
  mobileBreakpoint,
}: ResponsiveMenuProps) {
  const height = hooks.use100vh()
  const breakpoint = mobileBreakpoint ?? plume.BREAKPOINTS.TABLET

  const elementMeasuresRef = React.useRef<{ top: number; id: string; isActive?: boolean }[]>([])

  const isClientSide = hooks.useClientSide()
  const previousLocation = hooks.usePrevious(isClientSide ? window.location.href : '')
  plume.hooks.useIsoLayoutEffect(() => {
    if (isClientSide && previousLocation !== window.location.href) {
      setMobileNavAsOpen(false)
    }

    return () => {
      elementMeasuresRef.current = []
    }
  })

  const wrapperMeasureRef = React.useRef<{ height?: number; top: number }>({ top: 0 })
  const wrapperRef = React.useRef<HTMLDivElement>(null)
  const [closedMobileScroll, setClosedMobileScroll] = React.useState(0)

  const [isMobileNavOpen, setMobileNavAsOpen] = React.useState(false)

  const isDesktop = hooks.useDramaticallyInefficientMediaQuery(breakpoint)
  const [enableTransition, setEnableTransition] = React.useState(false)

  plume.hooks.useIsoLayoutEffect(() => {
    setTimeout(() => setEnableTransition(!isDesktop), 0)
  }, [isDesktop, closedMobileScroll])

  React.useEffect(() => {
    if (isDesktop) {
      setMobileNavAsOpen(false)
    }
  }, [isDesktop])

  // On ne peut pas utiliser le positionnement sticky du a la structure de la page projet,
  // c'est pourquoi on regarde aussi la position du footer
  const $contentRef = React.useRef<HTMLElement>(null)
  const $footerRef = React.useRef(typeof window !== 'undefined' ? document.querySelector('.b-footer') : null)
  const isIntersecting = hooks.useIsIntersecting($contentRef, { threshold: 0 }, true)
  const isIntersectingFooter = hooks.useIsIntersecting($footerRef, { threshold: 0 }, true)

  return (
    <Overlay
      className={className}
      height={height}
      openMobile={isMobileNavOpen}
      breakpoint={breakpoint}
      onClick={() => {
        if (wrapperRef.current) {
          wrapperRef.current.scrollTo({
            top: 0,
            behavior: 'smooth',
          })
        }
      }}
    >
      <MobileContainer
        closedMenuHeight={closedMenuHeight}
        labelMargin={labelMargin}
        enableTransition={enableTransition}
        openMobile={isMobileNavOpen}
        breakpoint={breakpoint}
      >
        <Wrapper
          breakpoint={breakpoint}
          height={wrapperMeasureRef.current.height}
          closedMenuHeight={closedMenuHeight}
          enableTransition={enableTransition}
          ref={(element) => {
            if (element instanceof HTMLDivElement) {
              const { top, height } = element.getBoundingClientRect()
              wrapperMeasureRef.current.top = top
              wrapperMeasureRef.current.height = wrapperMeasureRef.current.height ?? height
              ;(wrapperRef as any).current = element
            }
          }}
          scroll={closedMobileScroll}
          openMobile={isMobileNavOpen}
          onClickCapture={(event) => {
            if (!hooks.matchQuery(breakpoint) && !isMobileNavOpen) {
              event.stopPropagation()
              event.preventDefault()
              setMobileNavAsOpen(true)
            }
          }}
        >
          <Content
            containerRef={$contentRef}
            setActiveTopPosition={(top) => {
              const wrapperMeasure = wrapperMeasureRef.current
              setClosedMobileScroll(top - wrapperMeasure.top - labelMargin)
            }}
            isMobileNavOpen={isMobileNavOpen}
          />
        </Wrapper>
        {!isDesktop && (
          <CloseButton openMobile={isMobileNavOpen} onClick={() => setMobileNavAsOpen(false)}>
            <plume.styles.heading.XXXXXS>
              {t('Close menu')}
              <plume.glyphs.stroke.CaretUp size={10} />
            </plume.styles.heading.XXXXXS>
          </CloseButton>
        )}
        {isMobileNavOpen && !isDesktop && <GlobalStyleLockBody height={height} />}
        <plume.glyphs.stroke.BurgerMenu size={16} />
        <BackToTop
          breakpoint={breakpoint}
          withIcon={true}
          onClick={() => {
            scroll.animatedScrollTo(0, 150)
          }}
          isIntersecting={isIntersecting}
          isIntersectingFooter={isIntersectingFooter}
        >
          <plume.glyphs.stroke.ArrowRight size={13} />
          {t('Back to top')}
        </BackToTop>
      </MobileContainer>
      <MobileBorder breakpoint={breakpoint} closedMenuHeight={closedMenuHeight} />
    </Overlay>
  )
}

const MobileBorder = styled.span<{ closedMenuHeight: number; breakpoint: string }>`
  border-top: 1px solid ${plume.COLORS.PRIMARY_SAND_200};
  position: absolute;
  top: ${({ closedMenuHeight }) => closedMenuHeight}px;
  width: 100%;
  z-index: -1;

  @media screen and ${({ breakpoint }) => breakpoint} {
    display: none;
  }
`

type BackToTop = {
  isIntersecting: boolean
  isIntersectingFooter: boolean
  breakpoint: string
}

const BackToTop = styled(plume.LinkAsButton)<BackToTop>`
  display: none;

  @media screen and ${({ breakpoint }) => breakpoint} {
    bottom: 6px;
    display: block;
    left: 0;
    opacity: 1;
    padding-bottom: 20px;
    padding-left: 20px;
    position: fixed;
    text-transform: uppercase;
    transition: opacity 0.3s ease;
    width: ${PM_MENU_WIDTH};
    z-index: 3;

    ${plume.glyphs.stroke.ArrowRight} {
      color: ${plume.COLORS.PRIMARY_SAND_500};
      position: relative;
      top: -2px;
      transform: rotate(-90deg);
    }

    ${({ isIntersecting, isIntersectingFooter }) => {
      if (isIntersecting || isIntersectingFooter) {
        return css`
          opacity: 0;
          pointer-events: none;
        `
      }
    }}
  }
`

const Overlay = styled.nav<{ height: string | null; openMobile: boolean; breakpoint: string }>`
  background-color: ${plume.COLORS.hexToRgb(
    plume.COLORS.PRIMARY_GREY_900,
    0.4,
  )}; // TODO: Use COLORS.OVERLAY token when available
  bottom: 0;
  left: 0;
  right: 0;
  position: absolute;
  top: 60px;
  height: calc(${({ height }) => height ?? '100vh'} - 60px);
  transition: background-color 0.3s;
  z-index: ${plume.ZINDEX.NAVIGATION_MENU};

  @media screen and ${({ breakpoint }) => breakpoint} {
    height: auto;
    min-height: 100vh;
  }

  @media not screen and ${({ breakpoint }) => breakpoint} {
    ${(props) => {
      if (!props.openMobile) {
        return css`
          transition: bottom 0.3s;
          background-color: transparent;
          bottom: 100%;
          pointer-events: none;
        `
      } else {
        return css``
      }
    }}
  }

  @media screen and ${({ breakpoint }) => breakpoint} {
    background: transparent;
    height: auto;
    position: static;
    min-width: ${PM_MENU_WIDTH};
    width: ${PM_MENU_WIDTH};
    z-index: 2;
  }
`

type MobileContainerProps = {
  closedMenuHeight: number
  labelMargin: number
  openMobile: boolean
  enableTransition: boolean
  breakpoint: string
}

export const MobileContainer = styled.div<MobileContainerProps>`
  justify-content: start;
  flex-direction: column;
  display: flex;
  align-items: end;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  overflow: hidden;
  pointer-events: all;
  z-index: 2;

  &:after,
  &:before {
    content: '';
    height: 8px;
    left: 0;
    position: absolute;
    right: 0;
    z-index: 1;
    background-color: ${plume.COLORS.PRIMARY_GREY_000};
  }

  &:after {
    bottom: 0;
  }

  &:before {
    top: 0;
  }

  ${(props) => {
    if (props.enableTransition || props.openMobile) {
      return css`
        transition: bottom 0.3s;
      `
    }
  }};

  ${(props) => {
    if (!props.openMobile) {
      return css`
        bottom: calc(100% - ${props.closedMenuHeight}px);

        &:after,
        &:before {
          transition: opacity 0s ease-in 0.3s;
        }
      `
    } else {
      return css`
        &:after,
        &:before {
          opacity: 0;
        }
      `
    }
  }};

  ${plume.glyphs.stroke.BurgerMenu} {
    pointer-events: none;
  }

  & > ${plume.glyphs.stroke.BurgerMenu} {
    color: ${plume.COLORS.PRIMARY_SAND_500};
    opacity: 1;
    position: absolute;
    right: 16px;
    top: calc(${({ labelMargin }) => labelMargin}px + 3px);
    transition-duration: 0.3s;
    transition-property: opacity, visibility;
    transition-timing-function: cubic-bezier(0.55, 0, 1, 0.45);
    visibility: visible;

    ${({ openMobile }) => {
      if (openMobile) {
        return css`
          opacity: 0;
          transition-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
          visibility: hidden;
        `
      }
    }}
  }

  @media screen and ${({ breakpoint }) => breakpoint} {
    height: 100%;
    padding: 0;
    position: relative;

    & > ${plume.glyphs.stroke.BurgerMenu} {
      visibility: hidden;
    }

    &:after,
    &:before {
      content: none;
    }
  }
`

type WrapperProps = {
  closedMenuHeight: number
  enableTransition: boolean
  height?: number
  openMobile: boolean
  scroll: number
  breakpoint: string
}

export const Wrapper = styled.div<WrapperProps>`
  width: 100%;
  background: ${plume.COLORS.PRIMARY_GREY_000};
  clip-path: content-box;

  ${(props) => {
    if (props.enableTransition || props.openMobile) {
      return css`
        transition-property: height, margin-top;
        transition-duration: 0.3s;
      `
    }
  }}
  ${plume.styles.heading.Card1} {
    font-weight: inherit;
    color: inherit;
  }

  ${(props) => {
    if (props.openMobile) {
      return css`
        margin-top: 0;
        min-height: ${props.closedMenuHeight}px;
        height: calc(100% - 58px);
        overflow: auto;
      `
    } else {
      return css`
        margin-top: -${props.scroll}px;
        height: ${props.height !== undefined ? `${props.height}px` : 'auto'};
      `
    }
  }};

  @media screen and ${({ breakpoint }) => breakpoint} {
    transition: none;
    margin-top: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
    clip-path: none;
  }
`

const CloseButton = styled.button<{ openMobile: boolean }>`
  background: transparent;
  border: none;
  height: 58px;
  outline: none;
  overflow: hidden;
  padding: 0 30px;
  text-align: right;
  transition: height 0.3s;
  width: 100%;

  ${(props) => {
    if (!props.openMobile) {
      return css`
        height: 0;
      `
    }
  }}
  ${plume.styles.heading.XXXXXS} {
    color: ${plume.COLORS.PRIMARY_GREY_000};
  }

  ${plume.glyphs.stroke.CaretUp} {
    vertical-align: bottom;
    margin-left: 10px;
  }
`

const GlobalStyleLockBody = createGlobalStyle<{ height: string | null }>`
  body {
    height: ${({ height }) => height ?? '100vh'};
    box-sizing: border-box;
    overflow: hidden;
  }

  // HACK: to enable stickyness, iOS requires doubling 'overflow: hidden;' on both html & body tags.
  //  But this brings an undesirable side effect where the page jumps to top when opening the menu
  @supports (-webkit-overflow-scrolling: touch) {
    html {
      overflow: hidden;
    }
  }
`
